import React from 'react';
import { createRoot } from 'react-dom/client';
import { apply, remove } from '@directus/visual-editing';
import App from './App.tsx';
import './index.css';
import { initializeDirectusAutoLogin } from './services/autoLogin';
import { initSentry } from './config/sentry';
import { initAnalytics } from './config/analytics';
// Debug/test utilities — loaded in development only so they stay out of the
// production bundle and don't write to the production console.
if (import.meta.env.DEV) {
  import('./utils/directusRefresh'); // refresh utilities for debugging
  import('./utils/testDirectusSync'); // sync test utilities
}

// Initialize monitoring before anything else
initSentry();
initAnalytics();

// Initialize Directus autologin
initializeDirectusAutoLogin().catch(error => {
  console.error('Failed to initialize Directus autologin:', error);
});

// Connect the page to the Directus Visual Editor.
//
// IMPORTANT: `@directus/visual-editing`'s `apply()` registers the elements that
// have a `data-directus` attribute AT CALL TIME — it does NOT use a
// MutationObserver to pick up elements added later. In this SPA the editable
// markers are emitted by React after the initial render and after async content
// loads, so calling `apply()` once on startup would register nothing. We instead
// (re)run `apply()` whenever the set of `data-directus` elements changes, gated
// to the Studio iframe so normal browsing stays untouched.
if (typeof window !== 'undefined' && window.self !== window.top) {
  let markerCount = -1;
  let scheduled: ReturnType<typeof setTimeout> | null = null;

  // On save, the Studio hands us the EXACT field values that changed (the
  // payload is the just-committed value — `onSaved` fires after the write). Push
  // them straight into the matching fields: instant, and authoritative. We do
  // NOT then re-fetch those fields — a delayed server read races cache / commit
  // propagation and would overwrite the correct live value with a stale one
  // (the edit "reverts to the old text" a second later). The full refresh is a
  // fallback used ONLY when the Studio gave us no payload to apply directly.
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleFallbackRefresh = () => {
    if (refreshTimer !== null) return;
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      window.dispatchEvent(new CustomEvent('directus:refresh', { detail: { timestamp: Date.now() } }));
    }, 600);
  };
  const handleSaved = (data: { collection: string; item: string | number; payload: Record<string, unknown> }) => {
    const { collection, item, payload } = data ?? ({} as typeof data);
    const fields = payload && typeof payload === 'object' ? Object.entries(payload) : [];
    for (const [field, value] of fields) {
      window.dispatchEvent(new CustomEvent('directus:content-updated', {
        detail: { collection, itemId: item, field, value },
      }));
    }
    // Only fall back to a refetch when there was nothing to apply directly.
    if (fields.length === 0) scheduleFallbackRefresh();
  };

  const reapply = () => {
    scheduled = null;
    const count = document.querySelectorAll('[data-directus]').length;
    if (count === markerCount) return; // editable set unchanged — nothing to do
    markerCount = count;

    // Clear the previous registration, then re-scan the current DOM.
    try { remove(); } catch { /* nothing registered yet */ }
    apply({
      directusUrl: import.meta.env.VITE_DIRECTUS_URL || 'http://localhost:8065',
      onSaved: handleSaved,
    }).catch((error) => {
      console.warn('Directus Visual Editor not available:', error);
    });
  };

  const schedule = () => {
    if (scheduled === null) scheduled = setTimeout(reapply, 250);
  };

  const startObserving = () => {
    schedule(); // initial pass once content has had a chance to render
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-directus'],
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }
}

// Add postMessage handler for Directus Visual Editor
if (window.parent !== window) {
  // We're in an iframe (likely Directus Visual Editor)
  window.addEventListener('message', (event) => {
    // Accept messages from Directus domain
    const directusUrl = import.meta.env.VITE_DIRECTUS_URL || '';
    const allowedOrigins = [
      directusUrl,
      'https://keyprog.pt'
    ].filter(Boolean);
    
    if (allowedOrigins.includes(event.origin)) {
      try {
        const data = event.data;

        // Handle specific messages from Directus
        if (data && data.type === 'directus-visual-editor') {
          // Respond to Directus to confirm connection
          if (event.source && 'postMessage' in event.source) {
            (event.source as Window).postMessage(
              { type: 'directus-visual-editor-ready', status: 'ok' },
              event.origin
            );
          }
        }
      } catch (error) {
        console.error('Error handling postMessage:', error);
      }
    }
  });
  
  // Notify parent that we're ready
  try {
    window.parent.postMessage({ type: 'iframe-ready' }, '*');
  } catch (e) {
    console.warn('Could not send ready message to parent:', e);
  }
}

// PERFORMANCE FIX: Disable StrictMode in production to prevent double renders
const isProduction = import.meta.env.PROD;

createRoot(document.getElementById('root')!).render(
  isProduction ? (
    <App />
  ) : (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  )
);
